import { PlusIcon, RotateCcwIcon } from "lucide-react";
import { useState } from "react";
import { useGetDonationsLedgerQuery } from "../../../state/domainRouterApi";
import { TailwindDateField } from "../../../components/forms/TailwindDateField";
import { ModuleSectionNav } from "../../../components/navigation/ModuleSectionNav";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { OfflineDonationModal } from "./components/OfflineDonationModal";
import { QboExportButton } from "./components/QboExportButton";
import { RefundModal } from "./components/RefundModal";
import { DONATIONS_SECTION_ITEMS } from "./sectionNav";

const PRIMARY = "var(--primary)";
const TEXT = "var(--iron-grey)";
const BORDER = "var(--border)";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "REFUND_INITIATED", label: "Refund Initiated" },
  { value: "REFUNDED", label: "Refunded" },
  { value: "FAILED", label: "Failed" },
  { value: "OFFLINE_RECEIVED", label: "Offline Received" },
];

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((index) => (
        <div key={index} className="h-14 animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return <SupportErrorMessage>{message}</SupportErrorMessage>;
}

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return "$0.00";
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function resolveSponsorshipLabel(item: Record<string, unknown>): string {
  const campaignType = String(item.campaignType ?? "").trim();
  const sponsorshipTier = String(item.sponsorshipTier ?? "").trim();
  const divisionLabel = String(item.sponsorshipDivisionLabel ?? "").trim();

  if (sponsorshipTier === "one_division") {
    return divisionLabel ? `One division - ${divisionLabel}` : "One division";
  }
  if (sponsorshipTier === "schoolwide") {
    return "Schoolwide";
  }
  if (sponsorshipTier === "one_class") {
    return "One class";
  }
  return campaignType === "parnes-hayom" ? "One class" : "--";
}

type LedgerEntry = {
  id?: string;
  date?: string;
  hebrewDate?: string;
  donorName?: string;
  paymentMethod?: string;
  sponsorshipLabel?: string;
  amountCents?: number;
  status?: string;
};

export function SchoolDonationsLedgerPage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [offlineModalOpen, setOfflineModalOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState<string | null>(null);

  const filters: Record<string, unknown> = {};
  if (statusFilter) filters.status = statusFilter;
  if (dateFrom) filters.startDate = dateFrom;
  if (dateTo) filters.endDate = dateTo;

  const { data, isLoading, isError, error } =
    useGetDonationsLedgerQuery(filters);

  const entries: LedgerEntry[] = (() => {
    if (!data) return [];
    const candidate = data as Record<string, unknown>;
    if (Array.isArray(candidate.items)) {
      return (candidate.items as Array<Record<string, unknown>>).map(
        (item) => ({
          id: typeof item.id === "string" ? item.id : undefined,
          date:
            typeof item.targetGregorianDate === "string"
              ? item.targetGregorianDate
              : "--",
          hebrewDate:
            typeof item.hebrewDateLabel === "string"
              ? item.hebrewDateLabel
              : "--",
          donorName:
            [item.donorFirstName, item.donorLastName]
              .filter((value) => typeof value === "string" && value)
              .join(" ") || "Anonymous",
          paymentMethod:
            typeof item.method === "string"
              ? item.method
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "--",
          sponsorshipLabel: resolveSponsorshipLabel(item),
          amountCents:
            Number(item.grossCents ?? 0) + Number(item.feeRecoveryCents ?? 0),
          status: typeof item.status === "string" ? item.status : "UNKNOWN",
        }),
      );
    }
    return [];
  })();

  const statusColor = (status: string | undefined): React.CSSProperties => {
    switch (status) {
      case "PAID":
      case "OFFLINE_RECEIVED":
      case "OFFLINE_DEPOSITED":
        return { backgroundColor: "#dcfce7", color: "#166534" };
      case "PENDING":
      case "REFUND_INITIATED":
        return { backgroundColor: "#fef9c3", color: "#854d0e" };
      case "REFUNDED":
        return { backgroundColor: "#e0e7ff", color: "#3730a3" };
      case "FAILED":
        return { backgroundColor: "#fee2e2", color: "#991b1b" };
      default:
        return { backgroundColor: "#f3f4f6", color: TEXT };
    }
  };

  return (
    <main
      data-testid="school-donations-ledger"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-donations-v1.0"
      className="min-h-screen bg-[var(--background-light)]"
    >
      <YstRouteTransition className="mx-auto max-w-[1440px] space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="yst-surface-title">Donations Ledger</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setOfflineModalOpen(true)}
              className="yst-button-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold"
            >
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              Add Offline Donation
            </button>
            <QboExportButton
              entries={entries}
              filters={{
                status: statusFilter || undefined,
                dateRange:
                  dateFrom && dateTo
                    ? { from: dateFrom, to: dateTo }
                    : undefined,
              }}
            />
          </div>
        </div>

        <ModuleSectionNav
          items={DONATIONS_SECTION_ITEMS}
          activeId="ledger"
          ariaLabel="Donations sections"
          desktopClassName="source-faithful-tabs"
        />

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label
              htmlFor="ledger-status"
              className="mb-1 block text-xs font-semibold uppercase tracking-wider"
              style={{ color: `${TEXT}99` }}
            >
              Status
            </label>
            <select
              id="ledger-status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="yst-control-input px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="ledger-date-from"
              className="mb-1 block text-xs font-semibold uppercase tracking-wider"
              style={{ color: `${TEXT}99` }}
            >
              From
            </label>
            <TailwindDateField
              id="ledger-date-from"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="yst-control-input px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="ledger-date-to"
              className="mb-1 block text-xs font-semibold uppercase tracking-wider"
              style={{ color: `${TEXT}99` }}
            >
              To
            </label>
            <TailwindDateField
              id="ledger-date-to"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="yst-control-input px-3 py-2 text-sm"
            />
          </div>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <ErrorBanner
            message={
              (error && typeof error === "object" && "message" in error
                ? String((error as { message?: unknown }).message)
                : null) ??
              "Ledger data is not yet available. The donations backend has not been deployed."
            }
          />
        ) : entries.length === 0 ? (
          <div
            className="rounded-xl border bg-white py-16 text-center text-sm font-medium shadow-sm"
            style={{ borderColor: BORDER, color: TEXT }}
          >
            No ledger entries match the current filters.
          </div>
        ) : (
          <div className="yst-surface-panel overflow-hidden">
            <div className="source-faithful-tableScroll">
              <table
                className="min-w-[920px] divide-y text-sm"
                style={{ "--tw-divide-color": BORDER } as React.CSSProperties}
              >
                <thead>
                  <tr className="yst-surface-table-head">
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Date
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Hebrew Date
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Donor
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Method
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Sponsorship
                    </th>
                    <th
                      className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Amount
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Status
                    </th>
                    <th
                      className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider"
                      style={{ color: TEXT }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className="divide-y"
                  style={{ "--tw-divide-color": BORDER } as React.CSSProperties}
                >
                  {entries.map((entry) => (
                    <tr
                      key={entry.id ?? `${entry.date}-${entry.donorName}`}
                      className="yst-motion-color hover:bg-muted/70"
                    >
                      <td className="px-6 py-4" style={{ color: TEXT }}>
                        {entry.date ?? "--"}
                      </td>
                      <td className="px-6 py-4" style={{ color: TEXT }}>
                        {entry.hebrewDate ?? "--"}
                      </td>
                      <td
                        className="px-6 py-4 font-medium"
                        style={{ color: TEXT }}
                      >
                        {entry.donorName ?? "Anonymous"}
                      </td>
                      <td className="px-6 py-4" style={{ color: TEXT }}>
                        {entry.paymentMethod ?? "--"}
                      </td>
                      <td className="px-6 py-4" style={{ color: TEXT }}>
                        {entry.sponsorshipLabel ?? "--"}
                      </td>
                      <td
                        className="px-6 py-4 text-right font-medium"
                        style={{ color: TEXT }}
                      >
                        {formatCurrency(entry.amountCents)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold"
                          style={statusColor(entry.status)}
                        >
                          {entry.status === "REFUND_INITIATED"
                            ? "Refund pending"
                            : (entry.status ?? "unknown")}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {entry.status !== "REFUNDED" &&
                        entry.status !== "REFUND_INITIATED" &&
                        entry.id ? (
                          <button
                            type="button"
                            onClick={() => setRefundTarget(entry.id!)}
                            className="yst-motion-color inline-flex items-center gap-1 rounded-md border border-coral bg-white px-3 py-1.5 text-xs font-semibold text-strawberry hover:bg-warning-background"
                          >
                            <RotateCcwIcon
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                            Refund
                          </button>
                        ) : (
                          <span
                            className="text-xs"
                            style={{ color: `${TEXT}66` }}
                          >
                            --
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </YstRouteTransition>

      <OfflineDonationModal
        isOpen={offlineModalOpen}
        onClose={() => setOfflineModalOpen(false)}
        onSuccess={() => setOfflineModalOpen(false)}
      />

      {refundTarget ? (
        <RefundModal
          isOpen
          onClose={() => setRefundTarget(null)}
          donationId={refundTarget}
          onSuccess={() => setRefundTarget(null)}
        />
      ) : null}
    </main>
  );
}
